'use client'

import { motion } from 'framer-motion'
import { useAtomValue, useSetAtom, useStore } from 'jotai'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuthGuard } from '@/features/auth/hooks/use-auth-guard'
import { AccountConnectionSheet } from '@/features/dashboard/components/account-connection/account-connection-sheet'
import { useDashboardCommands } from '@/features/dashboard/hooks/use-dashboard-commands'
import { useNavigateToTradePage } from '@/features/dashboard/hooks/use-navigate-to-trade-page'
import type { PnlTimeframe } from '@/features/dashboard/lib/account-summary-display'
import {
  getArbitragePagePath,
  getAssistantPagePath,
  getHedgePagePath,
  getRiskSlidesPagePath,
  getScreenerPagePath,
} from '@/features/dashboard/lib/trade-page-path'
import { HomeActivity } from '@/features/home/components/home-activity'
import { HomeAskAi } from '@/features/home/components/home-ask-ai'
import { HomeBrief } from '@/features/home/components/home-brief'
import { HomeGettingStarted } from '@/features/home/components/home-getting-started'
import { HomeHero } from '@/features/home/components/home-hero'
import { HomeIdeasPanel } from '@/features/home/components/home-ideas-panel'
import { HomeMarkets } from '@/features/home/components/home-markets'
import { HOME_STAGGER } from '@/features/home/components/home-motion'
import { HomeNews } from '@/features/home/components/home-news'
import { HomePages } from '@/features/home/components/home-pages'
import { HomeMostTraded, HomePerformance } from '@/features/home/components/home-performance-panel'
import { HomePortfolio } from '@/features/home/components/home-portfolio'
import { HomeRiskCheck } from '@/features/home/components/home-risk-check'
import { HomeScenario } from '@/features/home/components/home-scenario'
import { HomeTools } from '@/features/home/components/home-tools'
import { HomeTrading } from '@/features/home/components/home-trading'
import { HomeTradingReview } from '@/features/home/components/home-trading-review'
import { HomeUpcoming } from '@/features/home/components/home-upcoming'
import { useHomeDailyBrief } from '@/features/home/hooks/use-home-daily-brief'
import { useHomeOpportunities } from '@/features/home/hooks/use-home-opportunities'
import { useHomePerformance } from '@/features/home/hooks/use-home-performance'
import { useHomeSnapshot } from '@/features/home/hooks/use-home-snapshot'
import { useHomeTradeJournal } from '@/features/home/hooks/use-home-trade-journal'
import { useHomeTradeStats } from '@/features/home/hooks/use-home-trade-stats'
import { buildDaySummaryPrompt, buildHabitsPrompt } from '@/features/home/lib/home-summary-prompt'
import { useGetPages } from '@/service/ui-state.hook'
import { activePageIdAtom } from '@/state/dashboard-layouts'
import { homeLastVisitAtom } from '@/state/home'

/**
 * Home: the starting point the side rail's first item opens. It answers, in order, "what is
 * going on with my money?" (portfolio, activity, risk), "what can I do here?" (the AI box, the
 * professional tools, the market) and "how do I learn it?" (the getting-started checklist) —
 * a hub that links into every part of the product instead of hiding them in the rail.
 *
 * Remembers when it was last left (and the portfolio value then) so the next visit can say
 * what changed in between.
 */
export function HomePage() {
  const router = useRouter()
  const store = useStore()
  const { isLoggedIn } = useAuthGuard()
  const [timeframe, setTimeframe] = useState<PnlTimeframe>('1D')
  const snapshot = useHomeSnapshot(timeframe)
  const dailyBrief = useHomeDailyBrief(snapshot, snapshot.openOrderSymbols)
  const opportunities = useHomeOpportunities({
    exposures: dailyBrief.exposures,
    risks: snapshot.risk.items,
    equityUsd: snapshot.portfolio.equityUsd,
    enabled: dailyBrief.ready,
  })
  // Shared with the trading panels through React Query, for the habits review prompt.
  const { stats: tradeStats } = useHomeTradeStats(30)
  const tradeJournal = useHomeTradeJournal()
  const { summary: performance } = useHomePerformance()
  const { addAccount, showAddAccount, setShowAddAccount, startResearchPrompt } =
    useDashboardCommands()
  const navigateToTradePage = useNavigateToTradePage()
  const activePageId = useAtomValue(activePageIdAtom)
  const { data: pagesData } = useGetPages({ enabled: isLoggedIn })

  // The previous visit as it was when Home opened; this visit is written when Home is left.
  const [previousVisit] = useState(() => store.get(homeLastVisitAtom))
  const setLastVisit = useSetAtom(homeLastVisitAtom)
  const equityRef = useRef(snapshot.portfolio.equityUsd)
  equityRef.current = snapshot.portfolio.equityUsd ?? equityRef.current
  useEffect(() => {
    const save = () => setLastVisit({ at: Date.now(), equityUsd: equityRef.current })
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') save()
    }
    window.addEventListener('pagehide', save)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      save()
      window.removeEventListener('pagehide', save)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [setLastVisit])

  // Back to the dashboard page the user was on (the side panel's own fallback chain).
  const openTrading = useCallback(() => {
    const pages = pagesData?.pages ?? []
    const target = activePageId ?? pages.find((page) => page.isActive)?.pageId ?? pages[0]?.pageId
    if (target) navigateToTradePage(target)
    else router.push('/trade')
  }, [activePageId, navigateToTradePage, pagesData?.pages, router])

  const askWhatHappened = useCallback(
    () =>
      startResearchPrompt(
        buildDaySummaryPrompt({
          equityUsd: snapshot.portfolio.equityUsd,
          pnl1dUsd: timeframe === '1D' ? snapshot.portfolio.pnlUsd : null,
          positions: snapshot.risk.items,
          openOrderCount: snapshot.openOrderCount,
          activity: snapshot.activity,
        })
      ),
    [snapshot, startResearchPrompt, timeframe]
  )

  const reviewHabits = useCallback(() => {
    if (!tradeStats) return
    startResearchPrompt(
      buildHabitsPrompt({
        volumeUsd: tradeStats.volumeUsd,
        fillCount: tradeStats.fillCount,
        makerShare: tradeStats.makerShare,
        feesPaidUsd: tradeStats.feesPaidUsd,
        feeSavingsUsd: tradeStats.feeSavingsUsd,
        topInstruments: tradeStats.topInstruments,
        upDays: performance?.upDays ?? null,
        downDays: performance?.downDays ?? null,
        maxDrawdownPercent: performance?.maxDrawdownPercent ?? null,
        leveragedPositions: snapshot.risk.flaggedCount,
        journal: tradeJournal.journal,
      })
    )
  }, [
    tradeStats,
    performance,
    snapshot.risk.flaggedCount,
    startResearchPrompt,
    tradeJournal.journal,
  ])

  const go = (path: string) => () => router.push(path)

  return (
    // The window scrolls (not a box of its own), so the page slides under the transparent top
    // bar rather than being cut off below it (exchange-tab-group.tsx).
    <div data-tutorial-page="home" className="px-3 pb-8 [--widget-radius:6px] md:px-6">
      <motion.div
        variants={HOME_STAGGER}
        initial="hidden"
        animate="show"
        className="mx-auto flex w-full max-w-400 flex-col gap-3"
      >
        <HomeHero
          snapshot={snapshot}
          previousVisit={previousVisit}
          onOpenTrading={openTrading}
          onConnectExchange={addAccount}
        />
        <HomeAskAi
          onAskWhatHappened={askWhatHappened}
          onReviewHabits={tradeStats && tradeStats.fillCount > 0 ? reviewHabits : undefined}
        />
        <div className="grid grid-cols-12 gap-3">
          <HomeBrief brief={dailyBrief} gauges={opportunities.gauges} className="col-span-12" />
          <HomeIdeasPanel
            brief={dailyBrief}
            opportunities={opportunities.ideas}
            opportunitiesPending={opportunities.isPending}
            opportunitiesAllowed={opportunities.allowed}
            className="col-span-12"
          />
          <HomeUpcoming brief={dailyBrief} className="col-span-12 lg:col-span-5" />
          <HomeScenario
            brief={dailyBrief}
            onOpenRiskSlides={go(getRiskSlidesPagePath())}
            className="col-span-12 lg:col-span-7"
          />
          <HomePortfolio
            snapshot={snapshot}
            timeframe={timeframe}
            onTimeframeChange={setTimeframe}
            onOpenTrading={openTrading}
            onOpenHedge={go(getHedgePagePath())}
            onConnectExchange={addAccount}
            className="col-span-12 xl:col-span-8"
          />
          <HomeGettingStarted
            snapshot={snapshot}
            onConnectExchange={addAccount}
            onOpenTrading={openTrading}
            className="col-span-12 xl:col-span-4"
          />
          <HomeActivity
            snapshot={snapshot}
            newSince={previousVisit?.at ?? null}
            onOpenTrading={openTrading}
            onAskWhatHappened={askWhatHappened}
            className="col-span-12 lg:col-span-6"
          />
          <HomeRiskCheck
            snapshot={snapshot}
            onOpenRiskSlides={go(getRiskSlidesPagePath())}
            onOpenHedge={go(getHedgePagePath())}
            className="col-span-12 lg:col-span-6"
          />
          <HomeTrading />
          <HomeTradingReview
            journal={tradeJournal.journal}
            isPending={tradeJournal.isPending}
            hasAccounts={tradeJournal.hasAccounts}
            onWriteReview={reviewHabits}
            className="col-span-12"
          />
          <HomePerformance className="col-span-12 lg:col-span-8" />
          <HomeMostTraded className="col-span-12 lg:col-span-4" />
          <HomeTools
            snapshot={snapshot}
            onOpenAssistant={go(getAssistantPagePath())}
            onOpenHedge={go(getHedgePagePath())}
            onOpenArbitrage={go(getArbitragePagePath())}
            onOpenRiskSlides={go(getRiskSlidesPagePath())}
            onOpenScreener={go(getScreenerPagePath())}
            className="col-span-12"
          />
          <HomeMarkets
            onOpenScreener={go(getScreenerPagePath())}
            className="col-span-12 xl:col-span-7"
          />
          <HomeNews className="col-span-12 xl:col-span-5" />
          <HomePages className="col-span-12" />
        </div>
      </motion.div>
      <AccountConnectionSheet open={showAddAccount} onOpenChange={setShowAddAccount} />
    </div>
  )
}
